import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  Plus, Search, Building2, MapPin, ExternalLink, MoreHorizontal,
  Edit, Trash2, Zap, RefreshCw, StopCircle, CheckCircle,
  AlertTriangle, Clock, Globe, Sparkles
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtDate = (d) => { try { return d ? format(parseISO(d), 'MMM d, h:mm a') : '-'; } catch { return '-'; } };
const fmtNum  = (n) => n != null ? Number(n).toLocaleString() : '0';

const VILLAGES = ['All', 'Southampton', 'East Hampton', 'Bridgehampton', 'Montauk', 'Sag Harbor', 'Amagansett', 'Wainscott', 'Water Mill', 'Quogue', 'Hampton Bays', 'Shelter Island', 'Other'];
const STATUSES = ['All', 'available', 'under_review', 'leased', 'off_market'];
const SPACE_TYPES = ['Restaurant-Ready', 'Raw Space', 'Retail', 'Mixed Use', 'Popup', 'Waterfront', 'Commercial', 'Other'];

const STATUS_COLORS = {
  available:     'badge-on-track',
  under_review:  'badge-due-today',
  leased:        'badge-overdue',
  off_market:    'bg-muted text-muted-foreground',
};

const HAMPTONS_AREAS = [
  'Southampton NY', 'East Hampton NY', 'Bridgehampton NY',
  'Montauk NY', 'Sag Harbor NY', 'Amagansett NY',
  'Water Mill NY', 'Hampton Bays NY', 'Westhampton Beach NY',
];

const EMPTY_FORM = { name: '', address: '', village: '', space_type: '', size_sqft: '', rent_monthly: '', source: '', status: 'available', listing_url: '', landlord_name: '', notes: '' };

export default function Spaces() {
  const [spaces, setSpaces]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [village, setVillage]         = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showModal, setShowModal]     = useState(false);
  const [editItem, setEditItem]       = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);

  // Discovery state
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [scrapeHistory, setScrapeHistory] = useState([]);
  const pollRef = useRef(null);

  const loadSpaces = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (village !== 'All') params.village = village;
      if (statusFilter !== 'All') params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get('/api/spaces', { params });
      setSpaces(res.data || []);
    } catch { toast.error('Failed to load spaces'); }
    finally { setLoading(false); }
  }, [village, statusFilter, search]);

  const loadScrapeStatus = useCallback(async () => {
    try {
      const [statusRes, histRes] = await Promise.all([
        api.get('/api/spaces/scrape/status'),
        api.get('/api/spaces/scrape/history'),
      ]);
      setScrapeStatus(statusRes.data);
      setScrapeHistory(histRes.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const timer = setTimeout(loadSpaces, 300);
    return () => clearTimeout(timer);
  }, [loadSpaces]);

  useEffect(() => { loadScrapeStatus(); }, [loadScrapeStatus]);

  // Poll while scraping
  useEffect(() => {
    if (scrapeStatus?.is_running) {
      pollRef.current = setInterval(() => {
        loadScrapeStatus();
        loadSpaces();
      }, 6000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [scrapeStatus?.is_running, loadScrapeStatus, loadSpaces]);

  const startScrape = async () => {
    try {
      const res = await api.post('/api/spaces/scrape/start');
      toast.success(res.data.message);
      setTimeout(loadScrapeStatus, 1000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start'); }
  };

  const stopScrape = async () => {
    try {
      await api.post('/api/spaces/scrape/stop');
      toast.info('Stop requested');
      setTimeout(loadScrapeStatus, 2000);
    } catch { toast.error('Failed to stop'); }
  };

  const openAdd  = () => { setEditItem(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (s) => {
    setEditItem(s);
    setForm({ name: s.name || '', address: s.address || '', village: s.village || '', space_type: s.space_type || '', size_sqft: s.size_sqft || '', rent_monthly: s.rent_monthly || '', source: s.source || '', status: s.status || 'available', listing_url: s.listing_url || '', landlord_name: s.landlord_name || '', notes: s.notes || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Space name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, size_sqft: form.size_sqft ? parseInt(form.size_sqft) : null, rent_monthly: form.rent_monthly ? parseFloat(form.rent_monthly) : null };
      if (editItem) { await api.put(`/api/spaces/${editItem.id}`, payload); toast.success('Space updated'); }
      else { await api.post('/api/spaces', payload); toast.success('Space added'); }
      setShowModal(false); loadSpaces();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this space?')) return;
    try { await api.delete(`/api/spaces/${id}`); toast.success('Deleted'); loadSpaces(); }
    catch { toast.error('Failed to delete'); }
  };

  const job   = scrapeStatus?.job;
  const stats = scrapeStatus?.stats || {};
  const isRunning = scrapeStatus?.is_running;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Commercial Spaces</h1>
          <p className="page-subtitle">{spaces.length} spaces tracked · {fmtNum(stats.auto_discovered || 0)} auto-discovered</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openAdd} className="bg-primary" data-testid="spaces-add-button">
            <Plus size={16} className="mr-1.5" /> Add Space
          </Button>
        </div>
      </div>

      <Tabs defaultValue="spaces">
        <TabsList className="mb-4">
          <TabsTrigger value="spaces" className="flex items-center gap-1.5">
            <Building2 size={13} /> All Spaces ({spaces.length})
          </TabsTrigger>
          <TabsTrigger value="discover" className="flex items-center gap-1.5">
            <Globe size={13} /> Auto-Discover
            {isRunning && <span className="w-2 h-2 rounded-full bg-primary animate-pulse ml-1" />}
          </TabsTrigger>
        </TabsList>

        {/* ══ SPACES LIST ══ */}
        <TabsContent value="spaces">
          {/* Filters */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search spaces..." className="pl-9" />
            </div>
            <Select value={village} onValueChange={setVillage}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Village" /></SelectTrigger>
              <SelectContent>{VILLAGES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s === 'All' ? 'All Status' : s.replace('_', ' ')}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
              ) : spaces.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 size={40} className="mx-auto mb-3 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No spaces found.</p>
                  <div className="flex gap-2 justify-center mt-3">
                    <Button onClick={openAdd} variant="outline" size="sm">Add manually</Button>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Space</TableHead>
                        <TableHead>Village</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Rent/Month</TableHead>
                        <TableHead>Landlord</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {spaces.map(s => (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {s.auto_discovered && (
                                <span title="Auto-discovered" className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">AI</span>
                              )}
                              <div>
                                <div>{s.name}</div>
                                {s.address && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={10} />{s.address}</div>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><Badge variant="secondary" className="text-xs">{s.village || '-'}</Badge></TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.space_type || '-'}</TableCell>
                          <TableCell className="text-sm">{s.size_sqft ? `${s.size_sqft.toLocaleString()} sqft` : '-'}</TableCell>
                          <TableCell className="text-sm">{s.rent_monthly ? `$${s.rent_monthly.toLocaleString()}/mo` : '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.landlord_name || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.source || '-'}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[s.status] || ''}`}>
                              {s.status?.replace('_', ' ')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="w-8 h-8"><MoreHorizontal size={16} /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(s)}><Edit size={14} className="mr-2" />Edit</DropdownMenuItem>
                                {s.listing_url && (
                                  <DropdownMenuItem onClick={() => window.open(s.listing_url, '_blank')}>
                                    <ExternalLink size={14} className="mr-2" />View Listing
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleDelete(s.id)} className="text-destructive">
                                  <Trash2 size={14} className="mr-2" />Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══ AUTO-DISCOVER ══ */}
        <TabsContent value="discover">
          <div className="space-y-5">
            {/* Live banner */}
            {isRunning && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20 fade-in">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-primary">Scraping in progress…</p>
                  <p className="text-xs text-muted-foreground">
                    Searching: <strong>{job?.current_area || '...'}</strong>
                    {job?.areas_done > 0 && ` · ${job.areas_done}/${HAMPTONS_AREAS.length} areas · ${job.total_found} listings found · ${job.new_added} new`}
                  </p>
                </div>
                <Button onClick={stopScrape} variant="outline" size="sm" className="flex-shrink-0">
                  <StopCircle size={13} className="mr-1.5 text-red-500" /> Stop
                </Button>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                <div className="flex items-center gap-2 mb-1"><Building2 size={14} className="text-primary" /></div>
                <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Cormorant Garamond,serif' }}>{fmtNum(stats.total_spaces)}</div>
                <div className="text-xs text-muted-foreground">Total Spaces in CRM</div>
              </Card>
              <Card className="p-4 bg-teal-50 border-teal-200">
                <div className="flex items-center gap-2 mb-1"><Sparkles size={14} className="text-teal-600" /></div>
                <div className="text-2xl font-bold tabular-nums text-teal-700" style={{ fontFamily: 'Cormorant Garamond,serif' }}>{fmtNum(stats.auto_discovered)}</div>
                <div className="text-xs text-muted-foreground">Auto-Discovered</div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-2 mb-1"><Globe size={14} className="text-muted-foreground" /></div>
                <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Cormorant Garamond,serif' }}>{HAMPTONS_AREAS.length}</div>
                <div className="text-xs text-muted-foreground">Areas Monitored</div>
              </Card>
            </div>

            {/* Active job progress */}
            {job && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                      {isRunning ? 'Current Scrape' : `Last Run — ${job.status}`}
                    </CardTitle>
                    <Badge variant="secondary" className={`text-xs ${
                      job.status === 'running' ? 'bg-primary/10 text-primary' :
                      job.status === 'completed' ? 'bg-green-100 text-green-700' :
                      'bg-muted text-muted-foreground'
                    }`}>{job.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{isRunning ? `Searching: ${job.current_area}` : 'Completed'}</span>
                      <span className="font-semibold">{job.progress_pct || 0}%</span>
                    </div>
                    <Progress value={job.progress_pct || 0} className="h-3" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{job.areas_done || 0} of {HAMPTONS_AREAS.length} areas searched</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 rounded-lg bg-muted/40">
                      <div className="text-xl font-bold tabular-nums text-foreground">{fmtNum(job.total_found)}</div>
                      <div className="text-xs text-muted-foreground">Listings Found</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-green-50 border border-green-200">
                      <div className="text-xl font-bold tabular-nums text-green-700">{fmtNum(job.new_added)}</div>
                      <div className="text-xs text-muted-foreground">New Spaces Added</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex gap-4">
                    {job.created_at && <span><Clock size={10} className="inline mr-1" />Started: {fmtDate(job.created_at)}</span>}
                    {job.completed_at && <span><CheckCircle size={10} className="inline mr-1 text-green-500" />Done: {fmtDate(job.completed_at)}</span>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Areas covered */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                  <MapPin size={14} className="text-primary" />
                  Hamptons Areas Searched
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {HAMPTONS_AREAS.map(area => (
                    <div key={area} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      job?.status === 'running' && job?.current_area === area
                        ? 'bg-primary/10 border-primary/30 text-primary'
                        : 'bg-muted border-border text-muted-foreground'
                    }`}>
                      {job?.status === 'running' && job?.current_area === area && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                      )}
                      {area.replace(' NY', '')}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Searches LoopNet, Crexi, CoStar, 42Floors, Douglas Elliman Commercial, Compass Commercial, and local Hamptons brokers. New spaces auto-added to your CRM and sent via RingCentral SMS.
                </p>
              </CardContent>
            </Card>

            {/* Start button */}
            {!isRunning ? (
              <Button onClick={startScrape} className="w-full bg-primary h-12 text-base" data-testid="start-scrape-btn">
                <Zap size={16} className="mr-2" /> Scrape All {HAMPTONS_AREAS.length} Hamptons Areas for New Commercial Spaces
              </Button>
            ) : (
              <Button onClick={stopScrape} variant="destructive" className="w-full h-12 text-base">
                <StopCircle size={16} className="mr-2" /> Stop Scraping
              </Button>
            )}

            {/* Run history */}
            {scrapeHistory.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Run History</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {scrapeHistory.map((run, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 text-sm">
                        <div className="flex items-center gap-3">
                          <Badge variant="secondary" className={`text-xs ${run.status === 'completed' ? 'bg-green-100 text-green-700' : ''}`}>
                            {run.status}
                          </Badge>
                          <span className="text-muted-foreground">{fmtDate(run.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{fmtNum(run.total_found)} found</span>
                          <span className="text-green-600 font-medium">+{fmtNum(run.new_added)} new</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {editItem ? 'Edit Space' : 'Add Commercial Space'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2"><Label>Space Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Main St Storefront" data-testid="space-form-name" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St, Southampton" /></div>
            <div className="space-y-1.5">
              <Label>Village</Label>
              <Select value={form.village} onValueChange={v => setForm(p => ({ ...p, village: v }))}>
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>{VILLAGES.slice(1).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Space Type</Label>
              <Select value={form.space_type} onValueChange={v => setForm(p => ({ ...p, space_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{SPACE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Size (sqft)</Label><Input type="number" value={form.size_sqft} onChange={e => setForm(p => ({ ...p, size_sqft: e.target.value }))} placeholder="2000" /></div>
            <div className="space-y-1.5"><Label>Monthly Rent ($)</Label><Input type="number" value={form.rent_monthly} onChange={e => setForm(p => ({ ...p, rent_monthly: e.target.value }))} placeholder="8000" /></div>
            <div className="space-y-1.5"><Label>Landlord Name</Label><Input value={form.landlord_name} onChange={e => setForm(p => ({ ...p, landlord_name: e.target.value }))} placeholder="Robert Johnson" /></div>
            <div className="space-y-1.5"><Label>Source</Label><Input value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} placeholder="LoopNet, Crexi, Direct..." /></div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="leased">Leased</SelectItem>
                  <SelectItem value="off_market">Off Market</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Listing URL</Label><Input value={form.listing_url} onChange={e => setForm(p => ({ ...p, listing_url: e.target.value }))} placeholder="https://loopnet.com/..." /></div>
            <div className="space-y-1.5 col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} placeholder="Notes about the space..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="space-save-button">
              {saving ? 'Saving...' : editItem ? 'Save Changes' : 'Add Space'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
